import { useQuery } from "@tanstack/react-query";

interface UseCityFromCoordinatesOptions {
  latitude?: string | number | null;
  longitude?: string | number | null;
  enabled?: boolean;
}

interface NominatimResponse {
  address?: {
    city?: string;
    state?: string;
  };
}

/**
 * Hook to fetch city name from latitude and longitude coordinates
 * Uses Nominatim OpenStreetMap API for reverse geocoding
 * Returns React Query result with cityName extracted from the response
 */
export function useCityFromCoordinates({
  latitude,
  longitude,
  enabled = true,
}: UseCityFromCoordinatesOptions) {
  // Normalize coordinates
  const lat =
    typeof latitude === "string" ? Number.parseFloat(latitude) : latitude;
  const lon =
    typeof longitude === "string" ? Number.parseFloat(longitude) : longitude;

  // Determine if query should be enabled
  const isValidCoordinates =
    lat &&
    lon &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lon) &&
    typeof lat === "number" &&
    typeof lon === "number";

  const queryEnabled = enabled && isValidCoordinates;

  const query = useQuery<NominatimResponse>({
    queryKey: ["cityFromCoordinates", lat, lon],
    queryFn: async () => {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1&accept-language=fa`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch city name");
      }
      return response.json();
    },
    enabled: !!queryEnabled,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour (city names don't change often)
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
    retry: 1,
  });

  // Extract city name from the response
  const cityName =
    query.data?.address?.city || query.data?.address?.state || null;

  return {
    cityName: query.isSuccess && cityName ? cityName : null,
    query,
  };
}
